import { differenceInDays, parseISO } from "date-fns";
import apiService from "../services/apiService";
import type { AssetInsuranceRecord } from "../types/postDisbursement";

export type SaveInsurancePolicyFormInput = Pick<
  AssetInsuranceRecord,
  "start_date" | "end_date" | "insurance_provider" | "premium"
> & Partial<Pick<
  AssetInsuranceRecord,
  "tenure_months" | "coordinator_name" | "coordinator_email" | "coordinator_phone"
>>;

type SaveInsurancePolicyInput = Omit<
  AssetInsuranceRecord,
  | "id"
  | "upload_policy_filename"
  | "upload_policy_s3_fileurl"
  | "upload_quotation_filename"
  | "upload_quotation_s3_fileurl"
  | "is_active"
  | "created_at"
  | "updated_at"
  | "created_by"
  | "deleted_by"
>;

const toScheduleNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const normalizeInsurancePolicy = (
  policy: Record<string, unknown>,
  fallbackIndividualAssetId?: string,
): AssetInsuranceRecord | null => {
  const start_date = String(policy.start_date ?? policy.coverFrom ?? policy.cover_from ?? "").trim();
  const end_date = String(policy.end_date ?? policy.coverTo ?? policy.cover_to ?? "").trim();
  const individual_asset_id = String(
    policy.individual_asset_id ?? policy.assetId ?? policy.asset_id ?? fallbackIndividualAssetId ?? "",
  ).trim();
  const id = String(policy.id ?? policy.insurance_id ?? policy.policy_id ?? "").trim();

  if (!start_date || !end_date || !individual_asset_id || !id) return null;

  return {
    id,
    shipment_id: policy.shipment_id != null ? String(policy.shipment_id) : null,
    customer_id: policy.customer_id != null ? String(policy.customer_id) : null,
    noa_id: (() => {
      const value = policy.noa_id;
      if (value == null || value === "") return null;
      if (typeof value === "number") return value;
      const text = String(value).trim();
      return text || null;
    })(),
    individual_asset_id,
    start_date,
    end_date,
    tenure_months: policy.tenure_months != null || policy.tenureMonths != null
      ? toScheduleNumber(policy.tenure_months ?? policy.tenureMonths)
      : undefined,
    insurance_provider: String(policy.insurance_provider ?? policy.insurer ?? "").trim(),
    premium: toScheduleNumber(policy.premium, 0),
    upload_policy_filename: policy.upload_policy_filename != null
      ? String(policy.upload_policy_filename)
      : policy.fileName != null
        ? String(policy.fileName)
        : undefined,
    upload_policy_s3_fileurl: policy.upload_policy_s3_fileurl != null
      ? String(policy.upload_policy_s3_fileurl)
      : policy.fileUrl != null
        ? String(policy.fileUrl)
        : undefined,
    upload_quotation_filename: policy.upload_quotation_filename != null
      ? String(policy.upload_quotation_filename)
      : policy.quotationFileName != null
        ? String(policy.quotationFileName)
        : undefined,
    upload_quotation_s3_fileurl: policy.upload_quotation_s3_fileurl != null
      ? String(policy.upload_quotation_s3_fileurl)
      : policy.quotationFileUrl != null
        ? String(policy.quotationFileUrl)
        : undefined,
    coordinator_name: policy.coordinator_name != null
      ? String(policy.coordinator_name)
      : policy.coordinatorName != null
        ? String(policy.coordinatorName)
        : undefined,
    coordinator_email: policy.coordinator_email != null
      ? String(policy.coordinator_email)
      : policy.coordinatorEmail != null
        ? String(policy.coordinatorEmail)
        : undefined,
    coordinator_phone: policy.coordinator_phone != null
      ? String(policy.coordinator_phone)
      : policy.coordinatorPhone != null
        ? String(policy.coordinatorPhone)
        : undefined,
    is_active: policy.is_active !== false,
    created_at: policy.created_at != null ? String(policy.created_at) : undefined,
    updated_at: policy.updated_at != null ? String(policy.updated_at) : undefined,
    created_by: policy.created_by != null ? String(policy.created_by) : undefined,
    deleted_by: policy.deleted_by != null ? String(policy.deleted_by) : null,
  };
};

export const normalizeInsurancePolicies = (
  policies: unknown,
  fallbackIndividualAssetId?: string,
): AssetInsuranceRecord[] => {
  if (!Array.isArray(policies)) return [];
  return policies
    .filter((policy): policy is Record<string, unknown> => !!policy && typeof policy === "object")
    .map((policy) => normalizeInsurancePolicy(policy, fallbackIndividualAssetId))
    .filter((policy): policy is AssetInsuranceRecord => policy != null);
};

const getResponseEnvelope = (response: unknown): Record<string, unknown> | null => {
  if (!response || typeof response !== "object" || Array.isArray(response)) return null;
  return response as Record<string, unknown>;
};

const toNonNegativeNumber = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export type InsuranceCoverageSummary = {
  totalTenureMonths: number | null;
  enteredMonths: number;
  remainingMonths: number | null;
};

export const extractInsurancePoliciesFromResponse = (
  response: unknown,
  fallbackIndividualAssetId?: string,
): AssetInsuranceRecord[] => {
  const envelope = getResponseEnvelope(response);
  const payload = envelope?.data ?? response;
  if (Array.isArray(payload)) {
    return normalizeInsurancePolicies(payload, fallbackIndividualAssetId);
  }
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const nested = record.insurance ?? record.policies ?? record.records;
    if (Array.isArray(nested)) {
      return normalizeInsurancePolicies(nested, fallbackIndividualAssetId);
    }
    if (record.id && record.start_date) {
      const single = normalizeInsurancePolicy(record, fallbackIndividualAssetId);
      return single ? [single] : [];
    }
  }
  return [];
};

export const extractCoverageSummaryFromResponse = (
  response: unknown,
  policies: AssetInsuranceRecord[] = [],
  fallbackTenureMonths = 12,
): InsuranceCoverageSummary => {
  const envelope = getResponseEnvelope(response);
  const totalFromApi = toNonNegativeNumber(
    envelope?.total_tenure_months ?? envelope?.totalTenureMonths,
  );
  const enteredFromApi = toNonNegativeNumber(
    envelope?.entered_months ?? envelope?.enteredMonths,
  );
  const remainingFromApi = toNonNegativeNumber(
    envelope?.remaining_months ?? envelope?.remainingMonths,
  );

  const totalTenureMonths = totalFromApi != null && totalFromApi > 0
    ? totalFromApi
    : (fallbackTenureMonths > 0 ? fallbackTenureMonths : null);
  const enteredMonths = enteredFromApi ?? sumPolicyTenureMonths(policies);
  const remainingMonths = remainingFromApi != null
    ? remainingFromApi
    : totalTenureMonths != null
      ? Math.max(0, totalTenureMonths - enteredMonths)
      : null;

  return { totalTenureMonths, enteredMonths, remainingMonths };
};

export const parseInsuranceAssetResponse = (
  response: unknown,
  fallbackIndividualAssetId?: string,
  contextFallback?: { customerId?: unknown; noaId?: unknown },
  fallbackTenureMonths = 12,
) => {
  const policies = extractInsurancePoliciesFromResponse(response, fallbackIndividualAssetId);
  const coverage = extractCoverageSummaryFromResponse(response, policies, fallbackTenureMonths);
  const context = extractInsuranceContextFromResponse(response, policies, contextFallback);
  return { policies, coverage, context };
};

export const fetchShipmentAssetInsurance = async (
  uniqueAssetId: string | number,
  fallbackContext?: { customerId?: unknown; noaId?: unknown },
  fallbackTenureMonths = 12,
) => {
  const response: unknown = await apiService.get(
    `/disbursement/get-individual-asset-insurance/${uniqueAssetId}`,
  );
  return parseInsuranceAssetResponse(
    response,
    uniqueAssetId,
    fallbackContext,
    fallbackTenureMonths,
  );
};

export const getLatestInsuranceEndDate = (
  policies: AssetInsuranceRecord[],
): string | undefined => {
  let latest: string | undefined;
  for (const policy of policies) {
    if (!policy.end_date) continue;
    if (!latest || new Date(policy.end_date).getTime() > new Date(latest).getTime()) {
      latest = policy.end_date;
    }
  }
  return latest;
};

type ShipmentInsuranceAsset = {
  id?: string | number;
  insurance_end_date?: string;
  customer_id?: unknown;
  noa_id?: unknown;
  noaId?: unknown;
};

export const enrichAssetsWithShipmentInsurance = async <T extends ShipmentInsuranceAsset>(
  assets: T[],
  fallbackContext?: { customerId?: unknown; noaId?: unknown },
): Promise<T[]> => {
  if (!assets.length) return assets;
  return Promise.all(
    assets.map(async (asset) => {
      const uniqueAssetId = asset?.id;
      if (uniqueAssetId == null || uniqueAssetId === "") return asset;
      try {
        const parsed = await fetchShipmentAssetInsurance(uniqueAssetId, {
          customerId: fallbackContext?.customerId ?? asset.customer_id,
          noaId: fallbackContext?.noaId ?? asset.noa_id ?? asset.noaId,
        });
        const latestEndDate = getLatestInsuranceEndDate(parsed.policies);
        if (!latestEndDate) return asset;
        return { ...asset, insurance_end_date: latestEndDate };
      } catch {
        return asset;
      }
    }),
  );
};

export const extractTenureMonthsFromResponse = (response: unknown, fallback = 12): number => {
  const envelope = getResponseEnvelope(response);
  const fromApi = toNonNegativeNumber(
    envelope?.total_tenure_months ?? envelope?.totalTenureMonths,
  );
  if (fromApi != null && fromApi > 0) return fromApi;

  const payload = (envelope?.data ?? response) as Record<string, unknown> | null | undefined;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const value = payload.tenure_months ?? payload.tenureMonths ?? payload.lease_tenure_months ?? payload.total_months;
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return fallback;
};

export type InsuranceAssetContext = {
  customer_id: string | null;
  noa_id: string | number | null;
};

const pickCustomerId = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text && text !== "null" && text !== "undefined" ? text : null;
};

const pickNoaId = (value: unknown): string | number | null => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value).trim();
  return text && text !== "null" && text !== "undefined" ? text : null;
};

export const extractInsuranceContextFromResponse = (
  response: unknown,
  policies: AssetInsuranceRecord[] = [],
  fallback?: { customerId?: unknown; noaId?: unknown },
): InsuranceAssetContext => {
  let customer_id: string | null = null;
  let noa_id: string | number | null = null;

  const tryRead = (obj: Record<string, unknown> | null | undefined) => {
    if (!obj) return;
    if (!customer_id) customer_id = pickCustomerId(obj.customer_id ?? obj.customerId);
    if (noa_id == null) noa_id = pickNoaId(obj.noa_id ?? obj.noaId);
  };

  const root = response as Record<string, unknown> | null | undefined;
  const envelope = getResponseEnvelope(response);
  const payload = envelope?.data ?? response;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    tryRead(payload as Record<string, unknown>);
  }
  tryRead(envelope);
  tryRead(root);

  if (!customer_id || noa_id == null) {
    const first = policies[0];
    if (first) {
      if (!customer_id) customer_id = pickCustomerId(first.customer_id);
      if (noa_id == null) noa_id = pickNoaId(first.noa_id);
    }
  }

  if (!customer_id) customer_id = pickCustomerId(fallback?.customerId);
  if (noa_id == null) noa_id = pickNoaId(fallback?.noaId);

  return { customer_id, noa_id };
};

export function getPolicyTenureMonths(policy: AssetInsuranceRecord): number {
  if (policy.tenure_months != null && policy.tenure_months > 0) return policy.tenure_months;
  if (policy.start_date && policy.end_date) {
    return Math.max(1, Math.round((differenceInDays(parseISO(policy.end_date), parseISO(policy.start_date)) + 1) / 30.44));
  }
  return 0;
}

export function sumPolicyTenureMonths(policies: AssetInsuranceRecord[]): number {
  return policies.reduce((sum, policy) => sum + getPolicyTenureMonths(policy), 0);
}

export function isFullyCovered(policies: AssetInsuranceRecord[], tenureMonths: number): boolean {
  const dated = policies.filter((p) => p.start_date && p.end_date);
  if (dated.length === 0) return false;
  const sorted = dated.slice().sort((a, b) => a.start_date.localeCompare(b.start_date));
  for (let i = 1; i < sorted.length; i++) {
    const prevEnd = parseISO(sorted[i - 1].end_date);
    const currStart = parseISO(sorted[i].start_date);
    if (differenceInDays(currStart, prevEnd) > 1) return false;
  }
  const start = parseISO(sorted[0].start_date);
  const end = parseISO(sorted[sorted.length - 1].end_date);
  const coveredDays = differenceInDays(end, start) + 1;
  const requiredDays = Math.round(tenureMonths * 30.44);
  return coveredDays >= requiredDays - 5;
}

export const buildSaveInsurancePolicyFormData = (input: {
  policy: SaveInsurancePolicyInput;
  policyFile?: File | null;
  quotationFile?: File | null;
  insuranceId?: string;
}) => {
  const formData = new FormData();
  const { policy } = input;

  if (input.insuranceId) formData.append("insurance_id", input.insuranceId);
  formData.append("noa_id", String(policy.noa_id ?? ""));
  formData.append("customer_id", policy.customer_id ?? "null");
  formData.append("shipment_id", policy.shipment_id ?? "null");
  formData.append("individual_asset_id", policy.individual_asset_id);
  formData.append("start_date", policy.start_date);
  formData.append("end_date", policy.end_date);
  if (policy.tenure_months != null) formData.append("tenure_months", String(policy.tenure_months));
  formData.append("insurance_provider", policy.insurance_provider);
  formData.append("premium", String(policy.premium));
  if (policy.coordinator_name) formData.append("coordinator_name", policy.coordinator_name);
  if (policy.coordinator_email) formData.append("coordinator_email", policy.coordinator_email);
  if (policy.coordinator_phone) formData.append("coordinator_phone", policy.coordinator_phone);

  if (input.policyFile) {
    formData.append("policy_file", input.policyFile);
    formData.append("upload_policy_filename", input.policyFile.name);
  }
  if (input.quotationFile) {
    formData.append("quotation_file", input.quotationFile);
    formData.append("upload_quotation_filename", input.quotationFile.name);
  }

  return formData;
};

export const inrFmt = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;
